import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Check, CircleDot, Save } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { StatusChip } from '../abf/components/StatusChip';
import { ViewportToast, type ActionFeedbackState } from '../abf/components/ActionFeedback';
import { DecisionBar, Header, Journey, Note, SmallButton, Surface } from '../abf/onboarding/review/contractReviewStyles';
import {
  type BookingContract, type TemplateJson, type VersionView, activate, draftFrom, getContracts, getVersion, propose,
  retire, returnToDraft, saveDraft, templateErrorMessage,
} from './documentTemplatesApi';
import TemplateFieldsPanel from './TemplateFieldsPanel';
import DryRunPanel from './DryRunPanel';

import TemplateDiffPanel from './TemplateDiffPanel';
import { TEMPLATES_PATH, stateOf, versionPath, when } from './templateModel';

/** F-PCP-038: what proved this version — every dry run made with it, newest first, with its verdict. */

const Tabs = styled.div`
  display: flex; gap: 4px; border-bottom: 1px solid #29374b; margin-bottom: 18px;
  button { padding: 10px 14px; background: none; border: 0; border-bottom: 2px solid transparent; color: #a8b6ca;
    font-size: 14px; font-weight: 600; cursor: pointer; }
  button[aria-selected='true'] { color: #e5edf7; border-bottom-color: #67c6f4; }
`;

const Details = styled.section`
  display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr); gap: 12px 16px; margin-bottom: 18px;
  label { display: flex; flex-direction: column; gap: 5px; color: #b6c7da; font-size: 12px; }
  input, textarea { padding: 8px 10px; min-height: 38px; background: #142033; color: #e5edf7; border: 1px solid #33465e;
    border-radius: 6px; font-size: 13px; font-family: inherit; }
  textarea { resize: vertical; line-height: 1.45; }  /* F-DT-001: the description reads in full */
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Reason = styled.div`
  display: flex; gap: 10px; align-items: center; flex: 1; justify-content: flex-end;
  input { flex: 1; max-width: 420px; padding: 8px 10px; min-height: 36px; background: #0f1b2b; color: #e5edf7;
    border: 1px solid #43617f; border-radius: 6px; }
`;

type Tab = 'fields' | 'try' | 'changes';
type Asking = 'return' | 'retire' | null;

/**
 * One version of a document template: its fields as the review page shows them, a dry run on a sample
 * document, what it changes against the version in use, and the one next step for its state.
 */
const DocumentTemplateVersionPage: React.FC = () => {
  const { id: idParam = '' } = useParams();
  const id = Number(idParam);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [view, setView] = useState<VersionView | null>(null);
  const [contracts, setContracts] = useState<Record<string, BookingContract>>({});
  const [working, setWorking] = useState<TemplateJson | null>(null);
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [hints, setHints] = useState('');
  const [tab, setTab] = useState<Tab>('fields');
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState<Asking>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<ActionFeedbackState | null>(null);

  const adopt = useCallback((next: VersionView) => {
    setView(next);
    setWorking(next.template.templateJson);
    setLabel(next.template.label);
    setDescription(next.template.description || '');
    setHints((next.template.filenameHints || []).join(', '));
  }, []);

  useEffect(() => {
    let alive = true;
    Promise.all([getVersion(id), getContracts()])
      .then(([loaded, registered]) => {
        if (!alive) return;
        adopt(loaded);
        setContracts(registered);
        setTab(loaded.template.status === 'PROPOSED' ? 'changes' : 'fields');
        setError(null);
      })
      .catch(failure => { if (alive) setError(templateErrorMessage(failure, 'This version could not be loaded')); });
    return () => { alive = false; };
  }, [id, adopt]);

  const template = view?.template;
  const editable = template?.status === 'DRAFT';
  const hintList = useMemo(() => hints.split(',').map(h => h.trim().toLowerCase()).filter(Boolean), [hints]);
  const dirty = Boolean(template && working && (
    JSON.stringify(working) !== JSON.stringify(template.templateJson)
    || label !== template.label
    || description !== (template.description || '')
    || JSON.stringify(hintList) !== JSON.stringify(template.filenameHints || [])
  ));
  const isMaker = Boolean(template && user && (
    template.contributors.some(name => name.toLowerCase() === user.username.toLowerCase())
    || template.createdBy?.toLowerCase() === user.username.toLowerCase()
    || template.proposedBy?.toLowerCase() === user.username.toLowerCase()
  ));

  const run = async (action: () => Promise<VersionView>, done: string, go?: (next: VersionView) => void) => {
    setBusy(true);
    try {
      const next = await action();
      setFeedback({ tone: 'success', message: done });
      setAsking(null);
      setReason('');
      if (go) go(next); else adopt(next);
    } catch (failure) {
      setFeedback({ tone: 'error', message: templateErrorMessage(failure, 'That did not go through') });
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <Surface>
        <Header><Link className="back" to={TEMPLATES_PATH}><ArrowLeft size={14} /> Document templates</Link></Header>
        <Note role="alert">{error}</Note>
      </Surface>
    );
  }
  if (!view || !template || !working) return <Surface><Note>Loading…</Note></Surface>;

  const state = stateOf(template.status);
  const problems = view.problems;
  const steps = [
    { key: 'DRAFT', title: 'Draft', note: 'Change it and try it on a document' },
    { key: 'PROPOSED', title: 'Second person', note: 'Another member of the team activates it' },
    { key: 'ACTIVE', title: 'In use', note: 'Read for every new document of this type' },
  ];
  const reached = ['DRAFT', 'PROPOSED', 'ACTIVE'].indexOf(template.status);

  const save = () => run(() => saveDraft(template.id, {
    label, description: description.trim(), filenameHints: hintList, templateJson: working,
  }), 'Draft saved');

  let primary: React.ReactNode = null;
  let message = view.nextAction;
  if (template.status === 'DRAFT') {
    if (dirty) {
      message = 'You have changes that are not saved yet';
      primary = <SmallButton type="button" disabled={busy} onClick={() => void save()}><Save size={14} /> Save changes</SmallButton>;
    } else {
      primary = (
        <SmallButton type="button" disabled={busy || problems.length > 0}
          onClick={() => void run(() => propose(template.id), `Version ${template.version} proposed`)}>
          Propose version {template.version}
        </SmallButton>
      );
      if (problems.length > 0) message = `Fix the ${problems.length} problem(s) marked in red, then propose`;
    }
  } else if (template.status === 'PROPOSED') {
    message = isMaker
      ? `You worked on version ${template.version}, so another member of the platform team activates it`
      : `Check the changes and the fields, then activate version ${template.version} for every new document`;
    primary = (
      <SmallButton type="button" disabled={busy || isMaker}
        onClick={() => void run(() => activate(template.id), `Version ${template.version} is in use`)}>
        <Check size={14} /> Activate version {template.version}
      </SmallButton>
    );
  } else {
    primary = (
      <SmallButton type="button" disabled={busy}
        onClick={() => void run(() => draftFrom(template.id), 'A new draft was started', next => navigate(versionPath(next.template.id)))}>
        Start a new draft from it
      </SmallButton>
    );
  }

  const secondary = template.status === 'PROPOSED' ? 'return' : template.status !== 'RETIRED' ? 'retire' : null;

  return (
    <Surface>
      <ViewportToast feedback={feedback} onClose={() => setFeedback(null)} />
      <Header>
        <Link className="back" to={TEMPLATES_PATH}><ArrowLeft size={14} /> Document templates</Link>
        <div className="heading">
          <div>
            <div className="eyebrow">{template.documentType}</div>
            <h1>{template.label} · version {template.version}</h1>
            <div className="meta">
              <StatusChip tone={state.tone} label={state.label} size="md" />
              <span>Started by {template.createdBy} {when(template.createdAt)}</span>
              {template.proposedBy && <span>Proposed by {template.proposedBy} {when(template.proposedAt)}</span>}
              {template.activatedBy && <span>Activated by {template.activatedBy} {when(template.activatedAt)}</span>}
              {template.fingerprint && <span title={template.fingerprint}>Fingerprint {String(template.fingerprint).slice(0, 12)}…</span>}
              {template.retiredBy && <span>Retired by {template.retiredBy} {when(template.retiredAt)}</span>}
              {template.statusNote && <span>Note: {template.statusNote}</span>}
            </div>
          </div>
        </div>
      </Header>

      {template.status !== 'RETIRED' && (
        <Journey aria-label="Where this version is">
          {steps.map((step, index) => (
            <li key={step.key} data-current={index === reached}>
              <span className={`step-icon${index < reached ? ' done' : ''}`}>
                {index < reached ? <Check size={15} /> : <CircleDot size={15} />}
              </span>
              <div><strong>{step.title}</strong><small>{step.note}</small></div>
            </li>
          ))}
        </Journey>
      )}

      {problems.length > 0 && (
        <Note role="alert" style={{ marginBottom: 16, borderColor: '#7f3b3b' }}>
          <strong>{problems.length} problem(s) stop this version going further.</strong>
          {problems.filter(p => !/^\/(fields|arrays|objects)\/\d+/.test(p.pointer)).map(p => (
            <p key={p.pointer + p.message}>{p.message}</p>
          ))}
          <p>Problems on a field are marked on that field below.</p>
        </Note>
      )}

      {editable && (
        <Details aria-label="Name and upload hints">
          <label>Name shown on upload<input value={label} onChange={e => setLabel(e.target.value)} maxLength={80} /></label>
          <label>Description<textarea rows={3} value={description} onChange={e => setDescription(e.target.value)} /></label>
          <label>Filename hints, separated by commas<input value={hints} onChange={e => setHints(e.target.value)} /></label>
        </Details>
      )}

      <Tabs role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'fields'} onClick={() => setTab('fields')}>Fields</button>
        <button type="button" role="tab" aria-selected={tab === 'try'} onClick={() => setTab('try')}>Try on a document</button>
        <button type="button" role="tab" aria-selected={tab === 'changes'} onClick={() => setTab('changes')}>Changes against the version in use</button>
      </Tabs>

      {tab === 'fields' && (
        <TemplateFieldsPanel template={working} editable={editable} contract={contracts[template.documentType]}
          problems={dirty ? [] : problems} onChange={setWorking} />
      )}
      {tab === 'try' && (
        <DryRunPanel versionId={template.id} version={template.version}
          blockedReason={template.status === 'RETIRED' ? 'A retired version cannot be tried; start a new draft from it.'
            : dirty ? 'Save your changes first: the document is read with the saved version.'
              : problems.length ? 'Fix the problems first: a version with problems cannot be read with.' : null} />
      )}
      {tab === 'changes' && <TemplateDiffPanel versionId={template.id} refreshKey={template.fingerprint + template.status} />}

      <DecisionBar>
        <div>
          <strong>{message}</strong>
          <span>{template.status === 'DRAFT' ? 'A draft is never used to read documents.' : state.label}</span>
        </div>
        {asking ? (
          <Reason>
            <input autoFocus value={reason} onChange={e => setReason(e.target.value)}
              placeholder={asking === 'return' ? 'What needs to change' : 'Why it is retired'} aria-label="Reason" />
            <SmallButton type="button" onClick={() => { setAsking(null); setReason(''); }}>Cancel</SmallButton>
            <SmallButton type="button" disabled={busy || !reason.trim()}
              onClick={() => void (asking === 'return'
                ? run(() => returnToDraft(template.id, reason.trim()), 'Returned to draft')
                : run(() => retire(template.id, reason.trim()), `Version ${template.version} retired`))}>
              {asking === 'return' ? 'Return to draft' : 'Retire'}
            </SmallButton>
          </Reason>
        ) : (
          <div style={{ display: 'flex', gap: 10 }}>
            {secondary && (
              <SmallButton type="button" style={{ borderColor: '#33465e' }} onClick={() => setAsking(secondary)}>
                {secondary === 'return' ? 'Return to draft' : 'Retire'}
              </SmallButton>
            )}
            {primary}
          </div>
        )}
      </DecisionBar>
    </Surface>
  );
};

export default DocumentTemplateVersionPage;
